import styled from 'styled-components';
import Layout from 'layouts/master-layout';
import { GetStaticProps } from 'next';
import { getFAQPage } from 'data/contentful-api';
import HeadlineBanner from 'components/headline-banner';
import MarkdownRender from 'components/markdown-render';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { useRouter } from 'next/router';
import i18n, { SettingsType } from 'utils/i18n';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';

type FAQPageProps = {
  faqPage: {
    title: string;
    text: string;
    mdxContactText: MDXRemoteSerializeResult<Record<string, unknown>>;
    faqItemsCollection: {
      items: Array<{
        sys: {
          id: string;
        };
        title: string;
        text: string;
        mdxText: MDXRemoteSerializeResult<Record<string, unknown>>;
      }>;
    };
  };
};

export const FAQPage: React.FC<FAQPageProps> = ({ faqPage }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  console.log('faqPage', faqPage);

  return (
    <Layout breadcrumb={[]} title={faqPage.title} description={faqPage.text}>
      <StyledFAQ>
        <HeadlineBanner wide title={faqPage.title} subtitle={faqPage.text} />

        <div className="outer-padding fixed-small-width">
          <AccordionRoot type="single" collapsible>
            {faqPage?.faqItemsCollection?.items
              ?.filter((item) => item && item.sys?.id)
              .map((item) => (
                <AccordionItem key={item.sys.id} value={item.sys.id}>
                  <AccordionHeader>
                    <AccordionTrigger>
                      {item.title}
                      <ChevronDownIcon aria-hidden />
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent>
                    <AccordionContentText>
                      <MarkdownRender markdown={item.mdxText} />
                    </AccordionContentText>
                  </AccordionContent>
                </AccordionItem>
              ))}
          </AccordionRoot>
          <MarkdownRender markdown={faqPage.mdxContactText} />
        </div>
      </StyledFAQ>
    </Layout>
  );
};

const StyledFAQ = styled.section`
  margin-bottom: 3rem;
`;

const AccordionRoot = styled(Accordion.Root)`
  width: 100%;
  margin: 0 auto;
`;

const AccordionItem = styled(Accordion.Item)`
  border: 1px solid var(--border);

  &:first-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled(Accordion.Header)`
  margin: 0;
`;

const AccordionTrigger = styled(Accordion.Trigger)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-base);
  cursor: pointer;
  /* transition: background 0.2s ease; */
  border: none;

  &[data-state='closed'] {
    background: #f5f5f5;
  }

  &[data-state='open'] {
    background: #f5f5f5;
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
    fill: var(--primary-base);
  }

  &[data-state='open'] svg {
    transform: rotate(180deg);
  }
`;

const AccordionContent = styled(Accordion.Content)`
  padding: 0 20px;
  overflow: hidden;

  &[data-state='open'] {
    animation: fadeIn 0.3s ease-out;
    padding: 20px 20px;
  }

  /* @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  } */
`;

const AccordionContentText = styled.div`
  color: var(--text);
  line-height: 1.6;
  font-size: 15px;
`;

export const getStaticProps: GetStaticProps<any> = async ({ locale }) => {
  const faqPage = await getFAQPage({ locale: locale as AcceptedLocales, preview: false });

  console.log('faqPage', faqPage);

  return {
    props: {
      faqPage,
    },
    revalidate: 1,
  };
};

export default FAQPage;
